import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BadgeCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES } from "@/lib/data";

export const Footer = () => {
  const [email, setEmail] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Enter a valid email");
    toast.success("You're on the list. First drop lands Friday.");
    setEmail("");
  };

  return (
    <footer className="relative overflow-hidden bg-ink text-slate-300 print:hidden">
      <div className="mx-auto max-w-7xl px-4 pb-10 pt-20 sm:px-8 lg:px-16">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <img
              src="/logo.png"
              alt="Oregon Tote Rentals"
              className="h-24 w-auto rounded-xl bg-white px-4 py-2.5 object-contain"
            />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
              Central Oregon's reusable moving tote rental. Sanitized, delivered to
              your door and picked up when you're unpacked — zero cardboard, zero waste.
            </p>
            <p className="mt-4 text-sm font-semibold text-white">
              Questions? Call{" "}
              <a href="tel:+15414087019" data-testid="footer-phone" className="text-royal transition-colors duration-200 hover:text-white">
                (541) 408-7019
              </a>
            </p>
            <form onSubmit={subscribe} className="mt-7 flex max-w-sm items-center gap-2">
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-input"
                placeholder="you@bendmail.com"
                className="h-11 w-full rounded-full border border-white/15 bg-white/5 px-4 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-200 focus:border-royal"
              />
              <button
                type="submit"
                data-testid="newsletter-submit"
                aria-label="Subscribe"
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-royal text-white transition-transform duration-200 hover:scale-105 active:scale-95"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>
          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-7">
            <div>
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-royal">
                Catalogue
              </p>
              <ul className="mt-5 space-y-3 text-sm">
                {CATEGORIES.slice(0, 5).map((c) => (
                  <li key={c}>
                    <Link
                      to={`/catalog?category=${encodeURIComponent(c)}`}
                      className="text-slate-400 transition-colors duration-200 hover:text-white"
                    >
                      {c}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-royal">
                Company
              </p>
              <ul className="mt-5 space-y-3 text-sm">
                <li><Link to="/" className="text-slate-400 transition-colors duration-200 hover:text-white">Home</Link></li>
                <li><Link to="/catalog" className="text-slate-400 transition-colors duration-200 hover:text-white">All packages</Link></li>
                <li><Link to="/account" className="text-slate-400 transition-colors duration-200 hover:text-white">My rentals</Link></li>
                <li><Link to="/agreement" className="text-slate-400 transition-colors duration-200 hover:text-white">Rental agreement</Link></li>
                <li><Link to="/reviews" className="text-slate-400 transition-colors duration-200 hover:text-white">Reviews</Link></li>
                <li><Link to="/auth" className="text-slate-400 transition-colors duration-200 hover:text-white">Sign in</Link></li>
                <li><Link to="/auth" data-testid="company-login-link" className="font-semibold text-slate-300 transition-colors duration-200 hover:text-white">Company login</Link></li>
              </ul>
            </div>
            <div>
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-royal">
                Assurance
              </p>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex items-center gap-2.5 text-slate-400">
                  <ShieldCheck className="h-4 w-4 text-royal" /> Damage waiver included
                </li>
                <li className="flex items-center gap-2.5 text-slate-400">
                  <BadgeCheck className="h-4 w-4 text-royal" /> Sanitized every rental
                </li>
                <li className="flex items-center gap-2.5 text-slate-400">
                  <Truck className="h-4 w-4 text-royal" /> Free metro delivery
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div
          aria-hidden
          className="text-outline-light pointer-events-none mt-16 select-none whitespace-nowrap text-center font-display text-[8vw] font-extrabold leading-none"
        >
          Oregon Tote Rentals
        </div>
        <div className="mt-6 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 text-xs text-slate-500 sm:flex-row">
          <p>© 2026 Oregon Tote Rentals. All rights reserved.</p>
          <p className="font-mono uppercase tracking-[0.2em]">Bend — Redmond — Sisters — Sunriver</p>
        </div>
      </div>
    </footer>
  );
};
